// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import { formatDate } from '@evolvemep/api-ts';
import { Flex, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router';

import { useMessages } from 'app/MessageCenter/MessagesContext';
import { hiPriNotificationPrefix } from 'app/MessageCenter/types';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { NOTIFICATIONS_ZINDEX } from 'components/Mantine/useMantineNotifications';
import { hasLengthAtLeast } from 'helpers/helper-types';

export const useShowHighPriorityMessages = () => {
  const {
    inMessageCenter,
    undismissedHighPriorityMessages,
    setUndismissedHighPriorityMessages,
    updateMessage,
    dismissAllHighPriority,
  } = useMessages();

  useEffect(() => {
    if (!inMessageCenter && hasLengthAtLeast(undismissedHighPriorityMessages, 1)) {
      const message = undismissedHighPriorityMessages[0];
      const id = `${hiPriNotificationPrefix}-${message.messageId}`;
      notifications.show({
        id,
        title: message.subject,
        message: (
          <>
            <Text fz="xs" mb={3}>
              {formatDate(message.createdOn, 'MMMM dd, yyyy')}
            </Text>
            <Flex justify="space-between">
              <Link className="link" to={`/message/${message.messageId}`} onClick={() => notifications.hide(id)}>
                View in message center
              </Link>
              {undismissedHighPriorityMessages.length > 1 && (
                <Text
                  className="link"
                  onClick={() => {
                    setUndismissedHighPriorityMessages([]);
                    notifications.hide(id);
                    void dismissAllHighPriority();
                  }}
                >
                  Dismiss all ({undismissedHighPriorityMessages.length})
                </Text>
              )}
            </Flex>
          </>
        ),
        position: 'top-right',
        icon: (
          <AgGridStyleTooltip
            label="High-priority notification"
            zIndex={NOTIFICATIONS_ZINDEX + 1}
            openDelay={0}
            withArrow
            position="left"
          >
            <EvolveIcon icon="Alert" inline color="white" />
          </AgGridStyleTooltip>
        ),
        autoClose: false,
        onClose: () => {
          void updateMessage(message.messageId, 'Dismiss');
        },
      });
    }
  }, [
    inMessageCenter,
    setUndismissedHighPriorityMessages,
    undismissedHighPriorityMessages,
    updateMessage,
    dismissAllHighPriority,
  ]);
};
